import { Button, Tour } from "antd";
import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import L, { Map } from "leaflet";
import { Position } from "@turf/turf";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { mapSteps } from "@/tour_steps/fields";
import { FarmMap } from "@/features/map/utils/farmmap";
import { locateMe } from "@/features/map/utils/geolocator";
import Coordinate from "@/components/coordinate/coordinate";
import { MapType } from "@/features/map/utils/agromap.type";
import { Farm } from "@/features/farm/domain/farm.interface";
import { IGeoShapeDetails } from "@/features/map/domain/IGeoShapeDetails";
import { Season } from "@/features/growing-season/domain/growing-season.interface";
// @ts-ignore
import Cancel from "@assets/icons/cancel.png";
// @ts-ignore
import Confirm from "@assets/icons/confirm.png";
// @ts-ignore
import Location from "@assets/icons/location.png";

interface AgroMapFarmProps {
  type: MapType;
  mapId: string;
  toolbarOptions: L.PM.ToolbarOptions;
  markerIcon: L.Icon<L.IconOptions> | L.DivIcon;
  farmerFarms?: Farm[];
  farmSeasons?: Season[];
  currentFarmPolygon?: Position[];
  samplingStationPoints?: { latitude: number; longitude: number }[];
  mapForWhatReport?: "EXPERIMENT" | "VISIT";
  onConfirm?: (data: IGeoShapeDetails) => void;
  onCancel?: () => void;
  onFarmClick?: (farmId: string) => void;
}

const AgroMapFarm = ({
  type,
  mapId,
  toolbarOptions,
  markerIcon,
  farmerFarms,
  farmSeasons,
  currentFarmPolygon,
  samplingStationPoints,
  mapForWhatReport,
  onConfirm,
  onCancel,
  onFarmClick,
}: AgroMapFarmProps) => {
  const popupRef = useRef<HTMLDivElement>(null);

  const farmMap = new FarmMap(
    popupRef,
    toolbarOptions,
    markerIcon,
    type,
    farmerFarms,
    farmSeasons,
    currentFarmPolygon
  );

  const [openTour, setOpenTour] = useState(false);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);

  const coordinateRef = useRef<(e: any) => Map | undefined>();

  useEffect(() => {
    if (document.getElementById(`map${mapId}`)) {
      farmMap.init(mapId);
      //   نمایش همه زمین های کشاورز
      farmerFarms?.forEach((farm) => {
        farmMap.mapPolygon(
          farm.polygon,
          false,
          !currentFarmPolygon?.length,
          -1,
          farm.id,
          onFarmClick
        );
      });
      farmSeasons?.forEach((season) => {
        farmMap.mapPolygon(
          season.polygon,
          false,
          false,
          season.product.productId!
        );
      });
      currentFarmPolygon &&
        currentFarmPolygon?.length > 0 &&
        farmMap.mapPolygon(currentFarmPolygon, type !== "onlyShow", true, -1);

      samplingStationPoints &&
        mapForWhatReport &&
        samplingStationPoints?.length > 0 &&
        farmMap.pinCircleBadge(samplingStationPoints, mapForWhatReport!);
      farmMap.initEditListeners();

      coordinateRef.current = (e: any) => farmMap.map?.flyTo(e);
    }
  }, [farmerFarms, farmSeasons, samplingStationPoints, currentFarmPolygon]);

  return (
    <div className="relative h-full w-full rounded-lg font-body">
      {/* map */}
      <div id={`map${mapId}`} className="h-full w-full rounded-lg" />

      {/* searchBox */}
      <div
        ref={ref1}
        id="searchAndHelp"
        className="flex max-lg:flex-col absolute gap-4 max-lg:gap-1 left-1/2 transform -translate-x-1/2 top-4 z-[999]"
      >
        {type !== "onlyShow" && (
          <Coordinate
            cityId={null}
            findCity={(e: any) => {
              console.log("dgfldkgjfjg");

              console.log({ e });
              coordinateRef.current!(e);
            }}
          />
        )}
      </div>

      {/* confirmation controls */}
      {type !== "onlyShow" && (
        <>
          <div
            ref={ref3}
            id="searchAndHelp"
            className="flex max-lg:flex-col absolute gap-4 max-lg:gap-1 left-0 max-lg:w-40 top-4 z-[999] "
          >
            <Button
              className="circle-neo flex items-center justify-center text-[#008a44] hover:!text-[#50aa44] font-bold !rounded-xl max-lg:w-16 max-lg:absolute left-0 top-20 !rounded-l-none"
              icon={<QuestionCircleOutlined className="text-base" />}
              onClick={() => setOpenTour(true)}
            >
              راهنما
            </Button>
          </div>
          <div
            id="confirmationControls"
            className="flex absolute gap-4 max-lg:gap-1 right-4 bottom-4 z-[999]"
          >
            <div
              onClick={onCancel}
              id="cancelButton"
              className="flex flex-row-reverse justify-between items-center bg-[#E9F0F7] h-9 w-20 max-lg:w-24 rounded-2xl px-3 cursor-pointer"
            >
              <img
                alt=""
                src={Cancel}
                className="h-8 w-8 max-lg:w-4 max-lg:h-4"
              />
              <p>لغو</p>
            </div>
            {/* confirm button, only shows when polygon created by user and it's length is not equal to zero*/}

            <div
              onClick={() => {
                const polygon =
                  farmMap.polygonDetails.polygon.length > 0
                    ? farmMap.polygonDetails.polygon
                    : currentFarmPolygon!;
                const validation = farmMap.validate(polygon);
                if (validation.isValid) {
                  onConfirm!(farmMap.polygonDetails);
                } else {
                  Swal.fire({
                    title: "خطا در رسم نقشه",
                    text: validation.message,
                    icon: "error",
                    showCloseButton: true,
                    confirmButtonText: "متوجه شدم",
                  });
                }
              }}
              id="confirmButton"
              className="flex flex-row-reverse justify-between items-center bg-[#E9F0F7] h-9 w-20 max-lg:w-24 rounded-full px-3 cursor-pointer"
            >
              <img
                alt=""
                src={Confirm}
                className="h-8 w-8 max-lg:w-4 max-lg:h-4"
              />{" "}
              <p>تایید</p>{" "}
            </div>
          </div>
        </>
      )}

      {/* popUp area */}
      <div
        ref={popupRef}
        id="popup"
        className="absolute hidden h-8 w-64 max-lg:w-fit max-lg:bottom-4 gap-4 left-5 bottom-3 z-[999] bg-[#E9F0F7] rounded-full justify-start items-center max-lg:pl-8 px-4"
      >
        <p></p>
      </div>
      {/* locator button */}
      {type !== "onlyShow" && (
        <div
          id="locator"
          className="circle-neo absolute flex justify-center items-center p-2 left-2 bottom-2.5 max-lg:bottom-3 max-lg:left-2 h-10 w-10 rounded-full z-[999] cursor-pointer"
          onClick={() => locateMe(farmMap.map!)}
        >
          <img src={Location} alt="" />
        </div>
      )}
      {/* tour guide */}
      <Tour
        type="primary"
        rootClassName="!bg-green-500"
        open={openTour}
        steps={mapSteps(ref1, ref2, ref3, ref4)}
        onClose={() => {
          setOpenTour(false);
        }}
      />
    </div>
  );
};

export default AgroMapFarm;
